import React, { useState, useEffect } from 'react';
import api from '../services/api';
import '../styles/CustomerFeedAvailability.css';

interface Feed {
  id: number;
  name: string;
  animal_type: string;
  feed_grade: string;
  feed_line?: string;
  is_enabled: boolean;
  is_normally_available?: boolean;
}

interface FeedAvailabilityData {
  customer_id: number;
  customer_type: string;
  custom_feeds: Feed[];
  standard_feeds: Feed[];
}

interface CustomerFeedAvailabilityProps {
  customerId: number;
  customerType: string;
  customerName: string;
}

// Helper to determine feed line from name
const getFeedLine = (feedName: string): string => {
  const name = feedName.toLowerCase();
  if (name.includes('five star')) return 'Five Star';
  if (name.includes('plus')) return 'Plus';
  if (name.includes('essentials') || name.includes('essential')) return 'Essentials';
  return 'Five Star'; // Default to Five Star (no "Standard" grade exists)
};

// Feed line badge colors
const feedLineBadgeClass: Record<string, string> = {
  'Five Star': 'feed-line-badge five-star',
  'Plus': 'feed-line-badge plus',
  'Essentials': 'feed-line-badge essentials',
};

// Feed line filter options
const feedLineOptions = [
  { value: 'all', label: 'All Feed Lines' },
  { value: 'essentials', label: 'Essentials' },
  { value: 'plus', label: 'Plus' },
  { value: 'five star', label: 'Five Star' },
];

const CustomerFeedAvailability: React.FC<CustomerFeedAvailabilityProps> = ({
  customerId,
  customerType,
  customerName,
}) => {
  const [data, setData] = useState<FeedAvailabilityData | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [bulkLoading, setBulkLoading] = useState(false);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedAnimalType, setSelectedAnimalType] = useState('');
  const [feedLineFilter, setFeedLineFilter] = useState('all');
  const [groupBy, setGroupBy] = useState<'animal_type' | 'feed_line'>('animal_type');
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set());

  // Track enabled/disabled feeds
  const [customFeedsEnabled, setCustomFeedsEnabled] = useState<Set<number>>(new Set());
  const [feedsDisabled, setFeedsDisabled] = useState<Set<number>>(new Set());

  useEffect(() => {
    loadFeedAvailability();
  }, [customerId]);

  const loadFeedAvailability = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await api.get(`/customers/${customerId}/feed-availability/`);

      setData(response.data);

      // Initialize enabled custom feeds
      const enabledCustom = new Set<number>(
        response.data.custom_feeds.filter((f: Feed) => f.is_enabled).map((f: Feed) => f.id)
      );
      setCustomFeedsEnabled(enabledCustom);

      // Initialize disabled standard feeds (feeds that are normally available but disabled)
      const disabled = new Set<number>(
        response.data.standard_feeds
          .filter((f: Feed) => f.is_normally_available && !f.is_enabled)
          .map((f: Feed) => f.id)
      );
      setFeedsDisabled(disabled);
    } catch (err: any) {
      console.error('Error loading feed availability:', err);
      setError('Failed to load feed availability');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError('');

      await api.post(`/customers/${customerId}/feed-availability/`, {
        custom_feeds_enabled: Array.from(customFeedsEnabled),
        feeds_disabled: Array.from(feedsDisabled),
      });

      alert('Feed availability updated successfully!');
      loadFeedAvailability(); // Reload to confirm
    } catch (err: any) {
      console.error('Error saving feed availability:', err);
      setError(err.response?.data?.detail || 'Failed to save feed availability');
    } finally {
      setSaving(false);
    }
  };

  const toggleCustomFeed = (feedId: number) => {
    const newSet = new Set(customFeedsEnabled);
    if (newSet.has(feedId)) {
      newSet.delete(feedId);
    } else {
      newSet.add(feedId);
    }
    setCustomFeedsEnabled(newSet);
  };

  const toggleStandardFeed = (feedId: number) => {
    // Admin can enable/disable ANY feed for ANY customer type
    const newSet = new Set(feedsDisabled);
    if (newSet.has(feedId)) {
      // Currently disabled, enable it
      newSet.delete(feedId);
    } else {
      // Currently enabled, disable it
      newSet.add(feedId);
    }
    setFeedsDisabled(newSet);
  };

  const isStandardFeedEnabled = (feed: Feed) => {
    // Feed is enabled if NOT in the disabled list
    return !feedsDisabled.has(feed.id);
  };

  const toggleSection = (animalType: string) => {
    const newSet = new Set(expandedSections);
    if (newSet.has(animalType)) {
      newSet.delete(animalType);
    } else {
      newSet.add(animalType);
    }
    setExpandedSections(newSet);
  };

  const expandAll = () => {
    if (!data) return;
    const allTypes = new Set(data.standard_feeds.map(f =>
      groupBy === 'feed_line' ? (f.feed_line || getFeedLine(f.name)) : f.animal_type
    ));
    setExpandedSections(allTypes);
  };

  const collapseAll = () => {
    setExpandedSections(new Set());
  };

  // Filter feeds by search, animal type, AND feed line
  const filterFeeds = (feeds: Feed[]) => {
    return feeds.filter(feed => {
      const matchesSearch = searchTerm === '' ||
        feed.name.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesAnimalType = selectedAnimalType === '' ||
        feed.animal_type === selectedAnimalType;
      const feedLine = feed.feed_line || getFeedLine(feed.name);
      const matchesFeedLine = feedLineFilter === 'all' ||
        feedLine.toLowerCase() === feedLineFilter.toLowerCase();
      return matchesSearch && matchesAnimalType && matchesFeedLine;
    });
  };

  const groupFeeds = (feeds: Feed[], groupField: 'animal_type' | 'feed_line') => {
    const groups: { [key: string]: Feed[] } = {};
    feeds.forEach(feed => {
      const key = groupField === 'feed_line'
        ? (feed.feed_line || getFeedLine(feed.name))
        : feed.animal_type;
      if (!groups[key]) {
        groups[key] = [];
      }
      groups[key].push(feed);
    });
    return groups;
  };

  // Bulk select all visible standard feeds
  const selectAllVisible = () => {
    if (!data) return;
    setBulkLoading(true);
    try {
      const filtered = filterFeeds(data.standard_feeds);
      const newDisabled = new Set(feedsDisabled);
      // Remove all filtered feeds from disabled set (enable them)
      filtered.forEach(feed => {
        newDisabled.delete(feed.id);
      });
      setFeedsDisabled(newDisabled);

      // Also enable filtered custom feeds
      const filteredCustom = filterFeeds(data.custom_feeds);
      const newCustomEnabled = new Set(customFeedsEnabled);
      filteredCustom.forEach(feed => {
        newCustomEnabled.add(feed.id);
      });
      setCustomFeedsEnabled(newCustomEnabled);
    } finally {
      setBulkLoading(false);
    }
  };

  // Bulk deselect all visible feeds
  const deselectAllVisible = () => {
    if (!data) return;
    setBulkLoading(true);
    try {
      const filtered = filterFeeds(data.standard_feeds);
      const newDisabled = new Set(feedsDisabled);
      // Add all filtered feeds to disabled set
      filtered.forEach(feed => {
        newDisabled.add(feed.id);
      });
      setFeedsDisabled(newDisabled);

      // Also disable filtered custom feeds
      const filteredCustom = filterFeeds(data.custom_feeds);
      const newCustomEnabled = new Set(customFeedsEnabled);
      filteredCustom.forEach(feed => {
        newCustomEnabled.delete(feed.id);
      });
      setCustomFeedsEnabled(newCustomEnabled);
    } finally {
      setBulkLoading(false);
    }
  };

  const selectAll = () => {
    if (!data) return;
    // Enable all custom feeds
    setCustomFeedsEnabled(new Set(data.custom_feeds.map(f => f.id)));
    // Enable all normally available standard feeds
    setFeedsDisabled(new Set());
  };

  const deselectAll = () => {
    // Disable all custom feeds
    setCustomFeedsEnabled(new Set());
    // Disable all standard feeds
    if (!data) return;
    const allStandardFeeds = new Set(data.standard_feeds.map(f => f.id));
    setFeedsDisabled(allStandardFeeds);
  };

  const resetToDefaults = () => {
    if (!window.confirm('Reset to default feed availability? This will undo any custom changes.')) {
      return;
    }
    // Disable all custom feeds
    setCustomFeedsEnabled(new Set());
    // Enable all normally available standard feeds
    setFeedsDisabled(new Set());
  };

  if (loading) {
    return <div className="feed-availability-loading">Loading feed availability...</div>;
  }

  if (!data) {
    return <div className="feed-availability-error">No data available</div>;
  }

  const filteredCustomFeeds = filterFeeds(data.custom_feeds);
  const filteredStandardFeeds = filterFeeds(data.standard_feeds);
  const groupedStandardFeeds = groupFeeds(filteredStandardFeeds, groupBy);
  const groupKeys = Object.keys(groupedStandardFeeds).sort();
  const allAnimalTypes = Array.from(new Set([...data.custom_feeds.map(f => f.animal_type), ...data.standard_feeds.map(f => f.animal_type)])).sort();

  // Calculate counts
  const totalVisibleStandard = filteredStandardFeeds.length;
  const enabledVisibleStandard = filteredStandardFeeds.filter(f => !feedsDisabled.has(f.id)).length;
  const totalVisibleCustom = filteredCustomFeeds.length;
  const enabledVisibleCustom = filteredCustomFeeds.filter(f => customFeedsEnabled.has(f.id)).length;
  const totalEnabled = data.standard_feeds.filter(f => !feedsDisabled.has(f.id)).length +
                       data.custom_feeds.filter(f => customFeedsEnabled.has(f.id)).length;
  const isFiltered = feedLineFilter !== 'all' || selectedAnimalType !== '' || searchTerm !== '';
  const filterLabel = feedLineFilter !== 'all' ? feedLineFilter : '';

  return (
    <div className="feed-availability-container">
      <div className="feed-availability-header">
        <h2>Feed Availability for {customerName}</h2>
        <p className="customer-type-badge">Customer Type: {customerType.replace(/_/g, ' ')}</p>
        <p className="feed-availability-description">
          Control which feeds this customer can order in their portal.
        </p>
      </div>

      {error && <div className="alert alert-error">{error}</div>}

      {/* Filters */}
      <div className="feed-availability-filters">
        <select
          value={groupBy}
          onChange={(e) => setGroupBy(e.target.value as 'animal_type' | 'feed_line')}
          className="filter-select"
        >
          <option value="animal_type">Group by Animal</option>
          <option value="feed_line">Group by Feed Line</option>
        </select>

        <select
          value={selectedAnimalType}
          onChange={(e) => setSelectedAnimalType(e.target.value)}
          className="filter-select"
        >
          <option value="">All Animals</option>
          {allAnimalTypes.map(type => (
            <option key={type} value={type}>{type}</option>
          ))}
        </select>

        <select
          value={feedLineFilter}
          onChange={(e) => setFeedLineFilter(e.target.value)}
          className="filter-select"
        >
          {feedLineOptions.map(opt => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>

        <input
          type="text"
          placeholder="Search feeds..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="search-input"
        />

        <button onClick={expandAll} className="btn-expand">Expand All</button>
        <button onClick={collapseAll} className="btn-collapse">Collapse All</button>
      </div>

      {/* Bulk Actions & Count */}
      <div className="bulk-actions-bar">
        <div className="feed-counts">
          <span className="count-badge">
            {enabledVisibleStandard + enabledVisibleCustom} of {totalVisibleStandard + totalVisibleCustom}
            {isFiltered ? ' filtered' : ''} feeds enabled
          </span>
          {isFiltered && (
            <span className="count-badge count-badge-secondary">
              ({totalEnabled} total enabled)
            </span>
          )}
        </div>
        <div className="bulk-buttons">
          <button
            onClick={selectAllVisible}
            disabled={bulkLoading}
            className="btn-bulk btn-bulk-select"
          >
            {bulkLoading ? 'Updating...' : `Select All${filterLabel ? ` ${filterLabel}` : ' Visible'}`}
          </button>
          <button
            onClick={deselectAllVisible}
            disabled={bulkLoading}
            className="btn-bulk btn-bulk-deselect"
          >
            {bulkLoading ? 'Updating...' : `Deselect All${filterLabel ? ` ${filterLabel}` : ' Visible'}`}
          </button>
        </div>
      </div>

      {/* Custom Feeds Section */}
      {filteredCustomFeeds.length > 0 && (
        <div className="feeds-section custom-feeds-section">
          <h3 className="section-title">CUSTOM FEEDS ({enabledVisibleCustom}/{totalVisibleCustom} enabled)</h3>
          <p className="section-description">
            These are custom formulas. Check to assign to this customer:
          </p>

          <div className="feeds-list">
            {filteredCustomFeeds.map(feed => {
              const feedLine = feed.feed_line || getFeedLine(feed.name);
              return (
                <div key={feed.id} className="feed-item">
                  <label className="feed-checkbox-label">
                    <input
                      type="checkbox"
                      checked={customFeedsEnabled.has(feed.id)}
                      onChange={() => toggleCustomFeed(feed.id)}
                    />
                    <span className="feed-name">{feed.name}</span>
                    <span className={feedLineBadgeClass[feedLine] || 'feed-line-badge standard'}>
                      {feedLine}
                    </span>
                    <span className="feed-grade">{feed.feed_grade}</span>
                    <span className="feed-animal-type">{feed.animal_type}</span>
                  </label>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Standard Feeds Section */}
      <div className="feeds-section standard-feeds-section">
        <h3 className="section-title">STANDARD FEEDS ({enabledVisibleStandard}/{totalVisibleStandard} enabled)</h3>
        <p className="section-description">
          These feeds are available by default. Uncheck to HIDE from this customer:
        </p>

        {groupKeys.length === 0 && (
          <div className="no-feeds-message">
            No feeds match the current filters.
          </div>
        )}

        {groupKeys.map(groupKey => {
          const feeds = groupedStandardFeeds[groupKey];
          const isExpanded = expandedSections.has(groupKey);
          const enabledCount = feeds.filter(f => !feedsDisabled.has(f.id)).length;

          return (
            <div key={groupKey} className="animal-type-group">
              <div
                className="animal-type-header"
                onClick={() => toggleSection(groupKey)}
              >
                <span className="expand-icon">{isExpanded ? '▼' : '▶'}</span>
                <span className="animal-type-name">
                  {groupKey} ({enabledCount}/{feeds.length} enabled)
                </span>
              </div>

              {isExpanded && (
                <div className="feeds-list">
                  {feeds.map(feed => {
                    const isEnabled = isStandardFeedEnabled(feed);
                    const feedLine = feed.feed_line || getFeedLine(feed.name);

                    return (
                      <div key={feed.id} className={`feed-item ${!isEnabled ? 'feed-not-available' : ''}`}>
                        <label className="feed-checkbox-label">
                          <input
                            type="checkbox"
                            checked={isEnabled}
                            onChange={() => toggleStandardFeed(feed.id)}
                          />
                          <span className="feed-name">{feed.name}</span>
                          <span className={feedLineBadgeClass[feedLine] || 'feed-line-badge standard'}>
                            {feedLine}
                          </span>
                          {groupBy === 'feed_line' && (
                            <span className="feed-animal-type">{feed.animal_type}</span>
                          )}
                          {feedsDisabled.has(feed.id) && (
                            <span className="feed-status-badge hidden">HIDDEN</span>
                          )}
                        </label>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Quick Actions */}
      <div className="feed-availability-actions">
        <div className="quick-actions">
          <button onClick={selectAll} className="btn-secondary">Select All Feeds</button>
          <button onClick={deselectAll} className="btn-secondary">Deselect All Feeds</button>
          <button onClick={resetToDefaults} className="btn-secondary">Reset to Defaults</button>
        </div>

        <button
          onClick={handleSave}
          disabled={saving}
          className="btn-primary btn-save"
        >
          {saving ? 'Saving...' : 'Save Feed Availability'}
        </button>
      </div>
    </div>
  );
};

export default CustomerFeedAvailability;
